import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';

import { ProcessDTO, useGetProcessesHook } from '@/api/generated';
import Autocomplete from '@/components/fields/Autocomplete';
import ChipMultiSelect from '@/components/fields/ChipMultiSelect';
import ChipSelect from '@/components/fields/ChipSelect';
import DateRange from '@/components/fields/DateRange/DateRange';
import Field from '@/components/fields/Field';
import GeneralStatusSelect from '@/components/fields/GeneralStatusSelect';
import NumericRange from '@/components/fields/NumericRange';
import { GeneralStatusEnum } from '@/components/statuses/GeneralStatus/types';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';
import CreateByDropdownSelect from '@/views/fields/UserDropdownSelect/CreatedByDropdownSelect';
import { ProcessSectionHiddenFields } from '@/views/Filters/constants';
import { getProcessFeatures, getProcessTypes } from '@/views/Filters/mocks/options';

import DropdownSection from './DropdownSection';

interface ProcessSectionProps {
  name: string;
  hiddenFields?: Set<ProcessSectionHiddenFields>;
}

const STATUSES = [
  GeneralStatusEnum.ACTIVE,
  GeneralStatusEnum.UNPUBLISHED,
  GeneralStatusEnum.ARCHIVED,
  GeneralStatusEnum.CANCELLED,
  GeneralStatusEnum.COMPLETED,
  GeneralStatusEnum.ON_HOLD
];

const ProcessSection: FC<ProcessSectionProps> = props => {
  const { t } = useTranslation('filters');
  const autosuggestProcesses = useGetProcessesHook();
  const { name, hiddenFields = new Set() } = props;

  const handleSearchProcesses = useCallback(
    async (query: string) => {
      try {
        const searchResults = await autosuggestProcesses({ search: query });

        return searchResults.map(({ name }: ProcessDTO) => ({
          label: name!,
          value: name!
        }));
      } catch (e) {
        return [];
      }
    },
    [autosuggestProcesses]
  );

  const isOrganizationShown = !hiddenFields.has(ProcessSectionHiddenFields.ORGANIZATION);
  const isFeaturesShown = !hiddenFields.has(ProcessSectionHiddenFields.FEATURES);
  const isTalentsCountShown = !hiddenFields.has(ProcessSectionHiddenFields.TALENTS_COUNT);
  const isCreatedByShown = !hiddenFields.has(ProcessSectionHiddenFields.CREATED_BY);

  return (
    <DropdownSection title={t('filters.sections.process.title')} {...props}>
      <Field
        component={Autocomplete}
        name={`${name}.processName`}
        label={t('filters.sections.process.name')}
        innerProps={{
          loadOptions: handleSearchProcesses,
          freeSolo: true
        }}
        withFormControl
      />
      <Divider />
      <Field
        component={ChipSelect}
        name={`${name}.processType`}
        label={t('filters.sections.process.type')}
        innerProps={{
          options: getProcessTypes(t),
          sx: { mx: -0.5 }
        }}
        withFormControl
      />
      <Divider />
      <Field
        component={GeneralStatusSelect}
        name={`${name}.status`}
        label={t('filters.sections.process.status')}
        innerProps={{
          activeOptions: STATUSES
        }}
        withFormControl
      />
      <Divider />
      {isOrganizationShown && (
        <>
          <Field
            component={OrganizationDropdownSelect}
            name={`${name}.organization`}
            label={t('filters.sections.process.organization')}
            innerProps={{
              isMulti: false,
              isClearable: true
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isFeaturesShown && (
        <>
          <Field
            component={ChipMultiSelect}
            name={`${name}.processFeatures`}
            label={t('filters.sections.process.features.title')}
            innerProps={{
              options: getProcessFeatures(t)
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      <Field
        component={DateRange}
        name={`${name}.createdAt`}
        label={t('filters.sections.process.creationDate')}
        withFormControl
      />
      <Divider />
      <Field
        component={DateRange}
        name={`${name}.updatedAt`}
        label={t('filters.sections.process.updateDate')}
        withFormControl
      />
      <Divider />
      {isTalentsCountShown && (
        <>
          <Field
            component={NumericRange}
            name={`${name}.talentsCount`}
            label={t('filters.sections.process.talentsCount')}
            innerProps={{
              min: 0,
              max: 1000
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isCreatedByShown && (
        <>
          <Field
            component={CreateByDropdownSelect}
            name={`${name}.createdBy`}
            label={t('filters.sections.process.createdBy')}
            withFormControl
          />
        </>
      )}
    </DropdownSection>
  );
};

export default ProcessSection;
